// src/components/Common/Lightbox.jsx
import { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import PropTypes from 'prop-types';
import './Lightbox.css';

/**
 * Lightbox Component
 * Full-screen image gallery viewer with keyboard and touch navigation
 * Uses React Portal for modal rendering and Framer Motion for animations
 */
const Lightbox = ({ images, currentIndex = 0, onClose }) => {
    const [activeIndex, setActiveIndex] = useState(currentIndex);
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);

    // Minimum swipe distance (in px) required for swipe detection
    const minSwipeDistance = 50;

    // Navigate to previous image
    const goToPrevious = useCallback(() => {
        setActiveIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    }, [images.length]);

    // Navigate to next image
    const goToNext = useCallback(() => {
        setActiveIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    }, [images.length]);

    // Handle keyboard navigation
    const handleKeyDown = useCallback(
        (e) => {
            switch (e.key) {
                case 'ArrowLeft':
                    goToPrevious();
                    break;
                case 'ArrowRight':
                    goToNext();
                    break;
                case 'Escape':
                    onClose();
                    break;
                default:
                    break;
            }
        },
        [goToPrevious, goToNext, onClose]
    );

    // Handle touch start
    const onTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    // Handle touch move
    const onTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    // Handle touch end (detect swipe)
    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe) {
            goToNext();
        } else if (isRightSwipe) {
            goToPrevious();
        }
    };

    // Prevent body scroll when lightbox is open
    useEffect(() => {
        document.body.style.overflow = 'hidden';

        return () => {
            document.body.style.overflow = '';
        };
    }, []);

    // Add keyboard event listener
    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleKeyDown]);

    // Update active index when currentIndex prop changes
    useEffect(() => {
        setActiveIndex(currentIndex);
    }, [currentIndex]);

    // Handle backdrop click
    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const lightboxContent = (
        <AnimatePresence>
            <motion.div
                className="lightbox-backdrop"
                onClick={handleBackdropClick}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
            >
                <div className="lightbox-container">
                    {/* Close Button */}
                    <button
                        className="lightbox-close"
                        onClick={onClose}
                        aria-label="Close lightbox"
                    >
                        <svg
                            width="24"
                            height="24"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>

                    {/* Image Counter */}
                    <div className="lightbox-counter">
                        {activeIndex + 1} / {images.length}
                    </div>

                    {/* Previous Button */}
                    {images.length > 1 && (
                        <button
                            className="lightbox-nav lightbox-nav-prev"
                            onClick={goToPrevious}
                            aria-label="Previous image"
                        >
                            <svg
                                width="32"
                                height="32"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <polyline points="15 18 9 12 15 6" />
                            </svg>
                        </button>
                    )}

                    {/* Image Display with Touch Support */}
                    <motion.div
                        className="lightbox-image-wrapper"
                        onTouchStart={onTouchStart}
                        onTouchMove={onTouchMove}
                        onTouchEnd={onTouchEnd}
                        key={activeIndex}
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        transition={{ duration: 0.3 }}
                    >
                        <img
                            src={images[activeIndex]}
                            alt={`Gallery image ${activeIndex + 1}`}
                            className="lightbox-image"
                        />
                    </motion.div>

                    {/* Next Button */}
                    {images.length > 1 && (
                        <button
                            className="lightbox-nav lightbox-nav-next"
                            onClick={goToNext}
                            aria-label="Next image"
                        >
                            <svg
                                width="32"
                                height="32"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <polyline points="9 18 15 12 9 6" />
                            </svg>
                        </button>
                    )}
                </div>
            </motion.div>
        </AnimatePresence>
    );

    // Render using portal to body
    return createPortal(lightboxContent, document.body);
};

Lightbox.propTypes = {
    images: PropTypes.arrayOf(PropTypes.string).isRequired,
    currentIndex: PropTypes.number,
    onClose: PropTypes.func.isRequired
};

export default Lightbox;
